// Game-like UI pieces drawn in code (owner, 2026-09-29): rounded cream panels with a soft brown
// border and a drop shadow, chunky 3D buttons (a darker lip under a rounded face) that grow on
// hover and press down on tap, rounded cards and a wooden title strip.
// Colours and sizes live in gameUiStyle.mjs. Labels and titles may carry {icon} tokens (ui/icons.mjs). Every piece is a Container, so callers add it to
// their own layer; `.hit` is the interactive shape where there is one.
import { PALETTE, RADIUS, LIP, FONT, buttonColors, buttonLabelY, progressFillWidth } from './gameUiStyle.mjs';
import { audioManager } from '../audio/AudioManager.js';
import { iconText } from './iconText.js';
import { GAME_WIDTH } from '../config.js';

function roundRect(g, x, y, w, h, r, fill, alpha = 1) {
  g.fillStyle(fill, alpha);
  g.fillRoundedRect(x, y, w, h, Math.min(r, h / 2, w / 2));
}

// A rounded panel centred on (x, y). Returns the container (origin at the centre). `fillAlpha` < 1 lets the
// scene show through the face (the shops, owner 2026-10-27): the border is then a stroke and there is no
// drop shadow, since both would show through it.
export function gamePanel(scene, x, y, w, h, { title = null, fillAlpha = 1 } = {}) {
  const c = scene.add.container(x, y);
  const g = scene.add.graphics();
  const P = PALETTE.panel;
  if (fillAlpha < 1) {
    roundRect(g, -w / 2 + 5, -h / 2 + 5, w - 10, h - 10, RADIUS.panel - 4, P.fill, fillAlpha);   // face
    g.lineStyle(5, P.border, 1);
    g.strokeRoundedRect(-w / 2 + 2.5, -h / 2 + 2.5, w - 5, h - 5, RADIUS.panel - 2);            // border
  } else {
    roundRect(g, -w / 2 + 4, -h / 2 + 8, w, h, RADIUS.panel, P.shadow, 0.28);          // drop shadow
    roundRect(g, -w / 2, -h / 2, w, h, RADIUS.panel, P.border);                       // border
    roundRect(g, -w / 2 + 5, -h / 2 + 5, w - 10, h - 10, RADIUS.panel - 4, P.fill);    // face
  }
  g.lineStyle(2, P.inner, 0.7);
  g.strokeRoundedRect(-w / 2 + 9, -h / 2 + 9, w - 18, h - 18, RADIUS.panel - 7);    // soft inner edge
  c.add(g);
  if (title) c.add(woodTitle(scene, 0, -h / 2 + 4, title, { maxWidth: w - 60 }));
  return c;
}

// A wooden strip with a title, centred on (x, y), overlapping a panel's top edge.
export function woodTitle(scene, x, y, label, { maxWidth = 600, fontSize = 22 } = {}) {
  const c = scene.add.container(x, y);
  const W = PALETTE.wood;
  const t = iconText(scene, 0, -2, label, { fontFamily: FONT, fontSize: `${fontSize}px`, color: W.text, fontStyle: 'bold', stroke: W.stroke, strokeThickness: 4 }).setOrigin(0.5);
  if (t.width > maxWidth - 40) t.setScale((maxWidth - 40) / t.width);
  const w = Math.min(maxWidth, t.displayWidth + 56), h = fontSize + 24;
  const g = scene.add.graphics();
  roundRect(g, -w / 2, -h / 2 + 4, w, h, 12, W.dark);
  roundRect(g, -w / 2, -h / 2, w, h, 12, W.fill);
  c.add([g, t]);
  return c;
}

// A chunky button centred on (x, y): a rounded face on a darker 3D lip, no shine (owner 2026-09-29). `variant`: primary | secondary | danger | dark.
// Returns the container with setLabel / setEnabled / setSelected helpers and `.hit`.
export function gameButton(scene, x, y, w, h, label, onClick, { variant = 'primary', fontSize = 18, enabled = true, radius = RADIUS.button } = {}) {
  const c = scene.add.container(x, y);
  const g = scene.add.graphics();
  const t = iconText(scene, 0, buttonLabelY(), label, { fontFamily: FONT, fontSize: `${fontSize}px`, fontStyle: 'bold', align: 'center' }).setOrigin(0.5);
  const hit = scene.add.rectangle(0, 0, w, h + LIP, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
  c.add([g, t, hit]);
  let state = { variant, enabled, pressed: false };
  const paint = () => {
    const col = buttonColors(state.variant, { enabled: state.enabled });
    const dy = state.pressed ? LIP - 1 : 0;
    g.clear();
    roundRect(g, -w / 2, -h / 2 + LIP, w, h, radius, col.lip);                 // the 3D lip
    roundRect(g, -w / 2, -h / 2 + dy, w, h, radius, col.face);                 // the face
    t.setColor(col.text).setY(buttonLabelY({ pressed: state.pressed }));
    if (col.stroke) t.setStroke(col.stroke, 3); else t.setStroke('#000000', 0);
    if (t.width > w - 16) t.setScale((w - 16) / t.width); else t.setScale(1);
  };
  paint();
  hit.on('pointerover', () => { if (state.enabled) c.setScale(1.04); });
  hit.on('pointerout', () => { c.setScale(1); state.pressed = false; paint(); });
  hit.on('pointerdown', () => { if (!state.enabled) return; state.pressed = true; paint(); });
  hit.on('pointerup', () => {
    if (!state.enabled || !state.pressed) return;
    state.pressed = false; paint();
    audioManager.playSfx?.('click');
    onClick?.();
  });
  c.hit = hit;
  c.setLabel = (s) => { t.setText(s); paint(); return c; };
  c.setEnabled = (on) => { state.enabled = !!on; hit.input && (hit.input.cursor = on ? 'pointer' : 'default'); paint(); return c; };
  c.setVariant = (v) => { state.variant = v; paint(); return c; };
  return c;
}

// A rounded card centred on (x, y) — the market / Schowek tile. Sized, so it can be made
// interactive directly (card.setInteractive / card.on('pointerup')).
export function gameCard(scene, x, y, w, h, { grey = false, selected = false } = {}) {
  const c = scene.add.container(x, y);
  const g = scene.add.graphics();
  roundRect(g, -w / 2 + 2, -h / 2 + 5, w, h, RADIUS.card, 0x000000, 0.18);
  roundRect(g, -w / 2, -h / 2, w, h, RADIUS.card, selected ? 0x6cbf45 : 0xc9a46a);
  roundRect(g, -w / 2 + 3, -h / 2 + 3, w - 6, h - 6, RADIUS.card - 2, grey ? 0xe6e0d6 : 0xffffff);
  c.add(g);
  c.setSize(w, h);          // the container itself is the tap target: card.setInteractive(...)
  c.hit = c;
  return c;
}

// The house / Podwórko upgrade button under the coin pill (owner 2026-09-30: subtler — smaller,
// the pill's wood instead of a loud green). One helper so both scenes look the same.
// The rounded wooden badge at the top centre: the farm's level (FarmHud, over its XP bar) and, on a friend's
// screens, whose they are (ui/visitHud.js) — its text in the middle there: `textY`. Returns { bg, text }.
export function hudBadge(scene, { textY = 22, depth = 1000 } = {}) {
  const cx = GAME_WIDTH / 2;
  const bg = scene.add.graphics().setScrollFactor(0).setDepth(depth);
  bg.fillStyle(0x000000, 0.25).fillRoundedRect(cx - 160 + 2, 6 + 4, 320, 50, 18);
  bg.fillStyle(0x6e4220, 1).fillRoundedRect(cx - 160, 6, 320, 50, 18);
  bg.fillStyle(0xb57a43, 1).fillRoundedRect(cx - 156, 9, 312, 44, 15);
  const text = iconText(scene, cx, textY, '', { fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '19px',
    color: '#fff8e8', fontStyle: 'bold', stroke: '#5a3414', strokeThickness: 4 })
    .setOrigin(0.5).setScrollFactor(0).setDepth(depth + 1);
  return { bg, text };
}

export function upgradeButton(scene, label, onClick, { depth = 50 } = {}) {
  const b = gameButton(scene, GAME_WIDTH - 125, 84, 210, 40, label, onClick, { variant: 'wood', fontSize: 15 });
  return b.setDepth(depth);
}

// Make a gameCard feel alive (owner, 2026-09-30: with sounds off a tap gave no sign of life). The
// card's picture / texts (`parts`, drawn at scene coords) move into the card so it scales as one:
// hover grows it and lights a golden outline, press dips it, release pops it and runs `onTap` a
// beat later (the pop is seen even when the tap closes the market). `onTap: null` = a card you
// can't take (locked / too dear): a short shake on tap instead.
export function cardFeedback(scene, card, parts, onTap, { cursor = null, gate = null } = {}) {
  for (const p of parts) { p.x -= card.x; p.y -= card.y; card.add(p); }
  const tween = (scale, duration, extra = {}) => {
    scene.tweens.killTweensOf(card);
    return scene.tweens.add({ targets: card, scale, duration, ease: 'Quad.easeOut', ...extra });
  };
  if (!onTap) {
    const x0 = card.x;
    card.setInteractive();
    card.on('pointerup', () => {
      scene.tweens.killTweensOf(card); card.x = x0;
      scene.tweens.add({ targets: card, x: x0 + 6, duration: 45, yoyo: true, repeat: 2, ease: 'Sine.easeInOut', onComplete: () => { card.x = x0; } });
    });
    return card;
  }
  const w = card.width, h = card.height;
  const glow = scene.add.graphics().setVisible(false);
  glow.lineStyle(4, 0xffc933, 1).strokeRoundedRect(-w / 2 - 2, -h / 2 - 2, w + 4, h + 4, RADIUS.card + 2);
  card.addAt(glow, 1);                                 // over the shadow + face, under the picture
  card.setInteractive(cursor ? { cursor } : { useHandCursor: true });
  let down = false;
  card.on('pointerover', () => { glow.setVisible(true); tween(1.04, 110); });
  card.on('pointerout', () => { glow.setVisible(false); down = false; tween(1, 110); });
  card.on('pointerdown', () => { down = true; tween(0.95, 70); });
  card.on('pointerup', () => {
    if (!down) return;                                 // a press that began elsewhere
    down = false;
    if (gate && !gate.begin()) return;                 // another card is acting / the panel closed (ui/tapGate.mjs)
    tween(1.08, 80, { yoyo: true, onComplete: () => card.active && card.setScale(glow.visible ? 1.04 : 1) });
    scene.time.delayedCall(120, () => {
      // An action that returns a promise (a server buy) holds the gate until it settles.
      const done = gate?.closed ? null : onTap();
      Promise.resolve(done).catch(() => {}).finally(() => gate?.end());
    });
  });
  return card;
}

// A rounded progress bar centred on (x, y): a dark sunken track and a green pill fill with a
// darker lower edge and a soft light stripe (the button look). `set(frac)` redraws the fill.
export function gameProgressBar(scene, x, y, w, h, { fill = 0x7ed957, lip = 0x4e9c2f, shine = 0xc8f5a8, track = 0x4a2c14 } = {}) {
  const c = scene.add.container(x, y);
  const bg = scene.add.graphics();
  roundRect(bg, -w / 2, -h / 2, w, h, h / 2, track);
  bg.fillStyle(0x000000, 0.25); bg.fillRoundedRect(-w / 2 + 2, -h / 2 + 1, w - 4, Math.max(2, h * 0.3), Math.min(h / 2, 3));  // inner shadow
  const fg = scene.add.graphics();
  c.add([bg, fg]);
  const pad = 2, iw = w - 2 * pad, ih = h - 2 * pad;
  let last = -1;
  c.set = (frac) => {
    const fw = progressFillWidth(frac, iw, ih);
    if (fw === last) return c;
    last = fw;
    fg.clear();
    if (fw > 0) {
      const x0 = -w / 2 + pad, y0 = -h / 2 + pad, r = ih / 2;
      roundRect(fg, x0, y0, fw, ih, r, lip);                                     // darker lower edge
      roundRect(fg, x0, y0, fw, ih - 2, r, fill);                                // the fill
      if (fw > ih) { fg.fillStyle(shine, 0.8); fg.fillRoundedRect(x0 + r / 2, y0 + 1.5, fw - r, Math.max(2, ih * 0.25), 1.5); }  // shine
    }
    return c;
  };
  return c;
}

// Page ‹ › for a card grid: big round wooden buttons either side (owner 2026-09-29: the plain
// text arrows were hard to see and to hit), greyed out at the first / last page.
export function pageArrows(scene, cx, y, halfSpan, page, pages, goTo, { size = 64 } = {}) {
  const btn = (x, icon, enabled, next) => gameButton(scene, x, y, size, size, icon, () => goTo(next),
    { variant: 'wood', fontSize: 30, enabled, radius: size / 2 });
  return [btn(cx - halfSpan, '{arrow_left}', page > 0, page - 1), btn(cx + halfSpan, '{arrow_right}', page < pages - 1, page + 1)];
}
